import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiLoader, FiTrash2, FiX, FiDownload, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { generateCharacterPhoto, getCharacterGallery, deletePhoto } from '../services/imageService.js';

const AdminCharacterForm = ({ character, onSubmit, isLoading, onCancel }) => {
  const { firebaseUser } = useAuth();

  const defaultExamples = [
    { user: '', char: '' },
    { user: '', char: '' },
    { user: '', char: '' }
  ];
  const [formData, setFormData] = useState({
    name: '',
    age: '25',
    sex: 'Female',
    ethnicity: '',
    hairColor: '',
    hairStyle: '',
    eyeColor: '',
    skinColor: '',
    bodyType: '',
    clothes: '',
    looks: '',
    occupation: '',
    personality: '',
    language: 'English',
    description: '',
    backstory: '',
    scenario: '',
    examples: defaultExamples,
    photoUrl: '',
    fullPhotoUrl: '',
    seed: '',
    displayOrder: 999,
    isActive: true
  });

  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmImage, setDeleteConfirmImage] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [galleryPage, setGalleryPage] = useState(0);
  const GALLERY_PAGE_SIZE = 5;

  // Fetch generated photos for this character from the generated_photos collection
  const fetchGalleryImages = useCallback(async () => {
    if (!character?.characterId || !firebaseUser) return;
    setGalleryLoading(true);
    try {
      const token = await firebaseUser.getIdToken();
      const result = await getCharacterGallery(token, character.characterId, null, 50);
      setGalleryImages(result.images || []);
    } catch (err) {
      console.error('Error fetching gallery images:', err);
    } finally {
      setGalleryLoading(false);
    }
  }, [character?.characterId, firebaseUser]);

  // Fetch gallery when character loads
  useEffect(() => {
    if (character?.characterId) {
      fetchGalleryImages();
    }
  }, [fetchGalleryImages, character?.characterId]);

  // Initialize form with character data when editing
  useEffect(() => {
    if (character) {
      setFormData({
        name: character.name || '',
        age: character.age || '25',
        sex: character.physicalTraits?.sex || 'Female',
        ethnicity: character.ethnicity || character.physicalTraits?.ethnicity || '',
        hairColor: character.physicalTraits?.hairColor || '',
        hairStyle: character.physicalTraits?.hairStyle || '',
        eyeColor: character.physicalTraits?.eyeColor || '',
        skinColor: character.physicalTraits?.skinColor || '',
        bodyType: character.physicalTraits?.bodyType || '',
        clothes: character.physicalTraits?.clothes || '',
        looks: character.physicalTraits?.looks || '',
        occupation: character.occupation || '',
        personality: character.personality || '',
        language: character.language || 'English',
        description: character.description || '',
        backstory: character.backstory || '',
        scenario: character.scenario || '',
        examples: Array.isArray(character.examples) && character.examples.length > 0
          ? character.examples.map(ex => ({ user: ex.user || '', char: ex.char || '' }))
          : defaultExamples,
        photoUrl: character.photoUrl || '',
        fullPhotoUrl: character.fullPhotoUrl || '',
        seed: character.seed ?? '',
        displayOrder: character.displayOrder ?? 999,
        isActive: character.isActive !== false
      });
    }
  }, [character]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Generate image — same params as CharacterModal
  const handleGenerateImage = async () => {
    if (!formData.name || !formData.sex || !formData.age) {
      setImageError('Name, Sex, and Age are required to generate a photo.');
      return;
    }

    try {
      setImageLoading(true);
      setImageError(null);

      const token = await firebaseUser.getIdToken();
      const characterId = character?.characterId || 'temp-' + Date.now();

      const baseParams = {
        characterId,
        sex: formData.sex,
        age: formData.age,
        race: formData.ethnicity,
        hairColor: formData.hairColor,
        hairType: formData.hairStyle,
        eyeColor: formData.eyeColor,
        skinColor: formData.skinColor,
        bodyType: formData.bodyType,
        clothes: formData.clothes,
        looks: formData.looks,
        description: formData.description,
        artStyle: 'realistic',
        generationContext: 'character_setup',
        seed: formData.seed ? Number(formData.seed) : undefined,
        width: 1024,
        height: 1024
      };

      // Generate portrait (avatar)
      const portraitResult = await generateCharacterPhoto(token, { ...baseParams, shotType: 'portrait' });
      // Generate full body
      const fullResult = await generateCharacterPhoto(token, { ...baseParams, shotType: 'full', height: 1280 });

      if (portraitResult.success && portraitResult.images?.length > 0 &&
          fullResult.success && fullResult.images?.length > 0) {
        const newPhotoUrl = portraitResult.images[0].thumbnailUrl || portraitResult.images[0].url;
        const newFullPhotoUrl = fullResult.images[0].url;
        setFormData(prev => ({ ...prev, photoUrl: newPhotoUrl, fullPhotoUrl: newFullPhotoUrl }));
      } else {
        setImageError('Image generation succeeded but returned no images.');
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setImageError(err.message || 'Failed to generate image');
    } finally {
      setImageLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a character name');
      return;
    }
    // Bundle physical traits same as CharacterModal
    const submitData = {
      ...formData,
      seed: formData.seed ? Number(formData.seed) : undefined,
      examples: formData.examples.map(ex => ({ user: ex.user || '', char: ex.char || '' })),
      physicalTraits: {
        sex: formData.sex,
        ethnicity: formData.ethnicity,
        hairColor: formData.hairColor,
        hairStyle: formData.hairStyle,
        eyeColor: formData.eyeColor,
        skinColor: formData.skinColor,
        bodyType: formData.bodyType,
        clothes: formData.clothes,
        looks: formData.looks
      }
    };
    onSubmit(submitData);
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1';

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <label className={labelClass}>Name *</label>
          <input type="text" name="name" value={formData.name} onChange={handleInputChange}
            placeholder="e.g., Aria" className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Age *</label>
          <input type="number" name="age" value={formData.age} onChange={handleInputChange}
            min="18" max="120" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Sex *</label>
          <select name="sex" value={formData.sex} onChange={handleInputChange} className={inputClass}>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
          </select>
        </div>
      </div>

      {/* Physical Traits — same as CharacterModal step 1 */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Physical Traits (used for image generation)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className={labelClass}>Ethnicity</label>
            <input type="text" name="ethnicity" value={formData.ethnicity} onChange={handleInputChange}
              placeholder="e.g. Asian, Latina" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Skin Tone</label>
            <input type="text" name="skinColor" value={formData.skinColor} onChange={handleInputChange}
              placeholder="e.g. Fair, Tan, Dark" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Hair Color</label>
            <input type="text" name="hairColor" value={formData.hairColor} onChange={handleInputChange}
              placeholder="e.g. Black, Blonde" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Hair Style</label>
            <input type="text" name="hairStyle" value={formData.hairStyle} onChange={handleInputChange}
              placeholder="e.g. Straight, Wavy" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Eye Color</label>
            <input type="text" name="eyeColor" value={formData.eyeColor} onChange={handleInputChange}
              placeholder="e.g. Brown, Blue" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Body Type</label>
            <input type="text" name="bodyType" value={formData.bodyType} onChange={handleInputChange}
              placeholder="e.g. Slim, Curvy" className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>General Looks</label>
            <textarea name="looks" value={formData.looks} onChange={handleInputChange}
              rows="2" placeholder="e.g. Beautiful, cute girl-next-door, average-looking, ruggedly handsome"
              className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Clothing Style</label>
            <textarea name="clothes" value={formData.clothes} onChange={handleInputChange}
              rows="2" placeholder="e.g. Casual, Business" className={inputClass} />
          </div>
        </div>
      </div>

      {/* Avatar section */}
      <div className="border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Existing Photos</h3>

        {/* Character document photos (photoUrl + fullPhotoUrl) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {formData.photoUrl && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Avatar (Portrait)</p>
              <img
                src={formData.photoUrl}
                alt={`${formData.name} avatar`}
                className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer"
                onClick={() => setViewImage({ url: formData.photoUrl, id: 'avatar' })}
              />
            </div>
          )}
          {formData.fullPhotoUrl && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Full Body</p>
              <img
                src={formData.fullPhotoUrl}
                alt={`${formData.name} full body`}
                className="w-32 h-40 object-cover rounded-lg border border-gray-200 cursor-pointer"
                onClick={() => setViewImage({ url: formData.fullPhotoUrl, id: 'fullbody' })}
              />
            </div>
          )}
        </div>

        {/* Generated photos gallery (from generated_photos collection) */}
        {character && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">Generated Photos (including chat images)</p>
            {galleryLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <FiLoader className="animate-spin" /> Loading...
              </div>
            ) : galleryImages.length > 0 ? (
              <>
                <div className="grid grid-cols-5 gap-2">
                  {galleryImages
                    .slice(galleryPage * GALLERY_PAGE_SIZE, (galleryPage + 1) * GALLERY_PAGE_SIZE)
                    .map((img) => (
                      <div key={img.id} className="relative group">
                        <img
                          src={img.thumbnailUrl || img.url}
                          alt="Generated"
                          className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer"
                          onClick={() => setViewImage(img)}
                        />
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmImage(img)}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-500 text-white rounded-full text-xs"
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    ))}
                </div>
                {galleryImages.length > GALLERY_PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <button
                      type="button"
                      onClick={() => setGalleryPage((p) => Math.max(0, p - 1))}
                      disabled={galleryPage === 0}
                      className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-30 hover:bg-gray-50"
                    >
                      <FiChevronLeft size={14} /> Prev
                    </button>
                    <span className="text-xs text-gray-500">
                      {galleryPage + 1} / {Math.ceil(galleryImages.length / GALLERY_PAGE_SIZE)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setGalleryPage((p) => p + 1)}
                      disabled={(galleryPage + 1) * GALLERY_PAGE_SIZE >= galleryImages.length}
                      className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-30 hover:bg-gray-50"
                    >
                      Next <FiChevronRight size={14} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400">No generated photos yet.</p>
            )}
          </div>
        )}

        {!formData.photoUrl && !formData.fullPhotoUrl && galleryImages.length === 0 && (
          <p className="text-sm text-gray-400 mb-3">No existing photos. Generate new ones below.</p>
        )}
        <div className="flex items-end gap-4 mt-3">
          <button type="button" onClick={handleGenerateImage}
            disabled={imageLoading || isLoading || !formData.sex || !formData.age}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition text-sm whitespace-nowrap">
            {imageLoading ? <><FiLoader className="mr-2 animate-spin" /> Generating...</> : 'Generate New Photos'}
          </button>
          <div className="w-32">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Seed</label>
            <input type="number" name="seed" value={formData.seed} onChange={handleInputChange}
              placeholder="random" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm" min="0" />
          </div>
        </div>
        {imageError && <p className="mt-2 text-red-600 text-sm">{imageError}</p>}
        {/* Manual URL override */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className={labelClass}>Photo URL (avatar)</label>
            <input type="text" name="photoUrl" value={formData.photoUrl} onChange={handleInputChange}
              placeholder="https://..." className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Full Photo URL</label>
            <input type="text" name="fullPhotoUrl" value={formData.fullPhotoUrl} onChange={handleInputChange}
              placeholder="https://..." className={inputClass} />
          </div>
        </div>
      </div>

      {/* Personality / Bio */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Occupation</label>
          <input type="text" name="occupation" value={formData.occupation} onChange={handleInputChange}
            placeholder="e.g. Nurse, Student" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Language</label>
          <input type="text" name="language" value={formData.language} onChange={handleInputChange}
            placeholder="e.g. English" className={inputClass} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea name="description" value={formData.description} onChange={handleInputChange}
          placeholder="Brief description shown in character grid" rows="2" className={inputClass} />
      </div>

      <div>
        <label className={labelClass}>Personality</label>
        <textarea name="personality" value={formData.personality} onChange={handleInputChange}
          placeholder="Character personality traits and behaviors" rows="3" className={inputClass} />
      </div>

      <div>
        <label className={labelClass}>Backstory</label>
        <textarea name="backstory" value={formData.backstory} onChange={handleInputChange}
          placeholder="Character background and history" rows="3" className={inputClass} />
      </div>

      <div>
        <label className={labelClass}>Scenario</label>
        <textarea name="scenario" value={formData.scenario} onChange={handleInputChange}
          placeholder="e.g. You are sitting at a dimly lit jazz bar. It's late evening and the band is on a break."
          rows="2" className={inputClass} />
        <p className="text-xs text-gray-400 mt-1">Describes the current setting and situation to anchor the character in a specific scene.</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Example Dialogues</label>
        <p className="text-xs text-gray-400 mb-3">Show the AI the desired tone and style with examples. This is more effective than rules.</p>
        {formData.examples.map((ex, i) => (
          <div key={i} className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">Example {i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="User says..."
                value={ex.user}
                onChange={(e) => {
                  const next = [...formData.examples];
                  next[i] = { ...next[i], user: e.target.value };
                  setFormData(prev => ({ ...prev, examples: next }));
                }}
                className={inputClass}
              />
              <input
                type="text"
                placeholder={`${formData.name || 'Character'} responds...`}
                value={ex.char}
                onChange={(e) => {
                  const next = [...formData.examples];
                  next[i] = { ...next[i], char: e.target.value };
                  setFormData(prev => ({ ...prev, examples: next }));
                }}
                className={inputClass}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
        <div>
          <label className={labelClass}>Display Order</label>
          <input type="number" name="displayOrder" value={formData.displayOrder} onChange={handleInputChange}
            min="0" className={inputClass} />
          <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange}
              className="w-4 h-4 text-indigo-600 rounded" />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4 border-t pt-4">
        <button type="button" onClick={onCancel} disabled={isLoading}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={isLoading}
          className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
          {isLoading ? <><FiLoader className="mr-2 animate-spin" /> Saving...</> : (character ? 'Update Character' : 'Create Character')}
        </button>
    </div>
    </form>

      {/* Delete confirmation modal */}
      {deleteConfirmImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={() => setDeleteConfirmImage(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Photo?</h3>
            <p className="text-sm text-gray-600 mb-4">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmImage(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setDeletingId(deleteConfirmImage.id);
                  try {
                    const token = await firebaseUser.getIdToken();
                    await deletePhoto(token, deleteConfirmImage.id);
                    setGalleryImages((prev) => prev.filter((i) => i.id !== deleteConfirmImage.id));
                    setDeleteConfirmImage(null);
                  } catch (e) {
                    console.error('Delete failed:', e);
                  } finally {
                    setDeletingId(null);
                  }
                }}
                disabled={deletingId === deleteConfirmImage.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {deletingId === deleteConfirmImage.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-size image preview modal */}
      {viewImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setViewImage(null)}>
          <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setViewImage(null)}
                className="p-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>
            <img
              src={viewImage.url}
              alt="Full size"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="flex justify-center mt-3">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const response = await fetch(viewImage.url);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `photo-${viewImage.id}.jpg`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('Download failed:', e);
                  }
                }}
                className="flex items-center gap-2 bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <FiDownload size={15} /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCharacterForm;
